import { useEffect, useState } from 'react';
import { Card, Spinner, Alert, Badge } from 'react-bootstrap';
import type { components } from '@telemedicine/shared';
import { listDecisionLog } from '../services/api';
import { personName } from '../people';
import { enumLabel } from '../labels';
import { getLang } from '../language';

type DecisionLogEntry = components['schemas']['DecisionLogEntry'];

/**
 * Read-only, chronological decision-log timeline for a case. The log is append-only and written
 * server-side on material actions (assign, recommendation, video, etc.), so this surfaces the
 * clinical audit trail. Rationale is a translatable pair (original + optional machine translation).
 */
export function CaseDecisionLog({
  caseId,
  refreshKey,
}: {
  caseId: string;
  /** Bump to force a reload after a case action appends a new entry (e.g. the case version). */
  refreshKey?: number | string;
}) {
  const lang = getLang();
  const [items, setItems] = useState<DecisionLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    listDecisionLog(caseId)
      .then((r) => setItems(r.items))
      .catch(() => setError('Failed to load the decision log'))
      .finally(() => setLoading(false));
  }, [caseId, refreshKey]);

  return (
    <Card className="shadow-sm mb-3">
      <Card.Header>Decision log</Card.Header>
      <Card.Body>
        {loading && (
          <div className="text-muted d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" /> Loading…
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && items.length === 0 && (
          <p className="text-muted mb-0">No decision-log entries yet.</p>
        )}

        {!loading && !error && items.length > 0 && (
          <ol className="list-unstyled mb-0">
            {items.map((e) => (
              <li key={e.entryId} className="border-start ps-3 pb-3 position-relative">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Badge bg="secondary">{enumLabel('decisionType', e.decisionType, lang)}</Badge>
                  <span className="text-muted small">
                    {new Date(e.createdAt).toLocaleString()}
                  </span>
                </div>
                <div>{e.rationale.original}</div>
                {e.rationale.translated && (
                  <div className="text-muted fst-italic mt-1">
                    {e.rationale.translated}{' '}
                    <Badge bg="info" className="align-middle">machine translated</Badge>
                  </div>
                )}
                <div className="text-muted small mt-1">
                  {personName(e.authorName, e.authorId)} · {e.authorRole}
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card.Body>
    </Card>
  );
}
